import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import moment from 'moment';

import DateRangeSelector from '../../../components/DateRangeSelector';

import {
  PROMO_TYPE_LABEL_IDS,
  ScheduledOfferPromoType,
  ScheduledOfferState,
} from '../models/ScheduledOfferModel';
import {
  PROMO_RESULTS_MARKETS,
  PromoResultsFilters,
  PromoResultsMetrics,
  PromoResultsRow,
  PromoResultsSummary,
} from '../models/ScheduledOfferResultsModel';
import { getScheduledOffersResults } from '../services/ScheduledOfferCRUD';
import ScheduledOfferOrdersModal, {
  formatAmount,
  formatShortDate,
} from './ScheduledOfferOrdersModal';

const ALL = 'all';
const ALL_PROMO_TYPES = Object.keys(
  PROMO_TYPE_LABEL_IDS
) as ScheduledOfferPromoType[];
// Every reported market bills in euros, so the totals can be summed safely.
const TOTALS_CURRENCY_SYMBOL = '€';
const API_DATE_FORMAT = 'YYYY-MM-DD';
// DateRangeSelector always needs a range: default to the last quarter.
const DEFAULT_RANGE_DAYS = 90;

interface FilterValues {
  /** Charge date range, both ends included. */
  since: Date;
  until: Date;
  market: string;
  promoType: typeof ALL | ScheduledOfferPromoType;
  state: typeof ALL | ScheduledOfferState;
}

const initialFilters: FilterValues = {
  since: moment().subtract(DEFAULT_RANGE_DAYS, 'days').toDate(),
  until: new Date(),
  market: ALL,
  promoType: ALL,
  state: ALL,
};

function toQueryParams(values: FilterValues): PromoResultsFilters {
  return {
    date_from: moment(values.since).format(API_DATE_FORMAT),
    date_to: moment(values.until).format(API_DATE_FORMAT),
    country:
      values.market === ALL ? PROMO_RESULTS_MARKETS.join(',') : values.market,
    promo_type: values.promoType === ALL ? undefined : values.promoType,
    state: values.state === ALL ? undefined : values.state,
  };
}

interface MetricsCardProps {
  title: string;
  metrics: PromoResultsMetrics;
  footnote?: string;
}

function MetricsCard({ title, metrics, footnote }: MetricsCardProps) {
  const intl = useIntl();
  const items = [
    {
      labelId: 'promoResults.subscribers',
      value: intl.formatNumber(metrics.subscribers),
    },
    {
      labelId: 'promoResults.orders',
      value: intl.formatNumber(metrics.orders),
    },
    {
      labelId: 'promoResults.grossRevenue',
      value: formatAmount(intl, metrics.gross_revenue, TOTALS_CURRENCY_SYMBOL),
    },
    {
      labelId: 'promoResults.netRevenue',
      value: formatAmount(intl, metrics.net_revenue, TOTALS_CURRENCY_SYMBOL),
    },
    {
      labelId: 'promoResults.discount',
      value: formatAmount(
        intl,
        metrics.discount_inc_tax,
        TOTALS_CURRENCY_SYMBOL
      ),
    },
  ];

  return (
    <div
      className="border rounded p-5 flex-grow-1"
      style={{ minWidth: '260px' }}
    >
      <h4 className="mb-4">{title}</h4>
      {items.map((item) => (
        <div key={item.labelId} className="d-flex justify-content-between">
          <span className="text-muted">
            {intl.formatMessage({ id: item.labelId })}
          </span>
          <span className="fw-bolder">{item.value}</span>
        </div>
      ))}
      {footnote && <div className="text-warning fs-7 mt-3">{footnote}</div>}
    </div>
  );
}

export default function ScheduledOfferResultsTable() {
  const intl = useIntl();
  const [values, setValues] = useState<FilterValues>(initialFilters);
  // Filters of the report on screen, so the orders modal uses the same range
  // even if the form was edited afterwards without searching again.
  const [appliedFilters, setAppliedFilters] = useState<PromoResultsFilters>(
    toQueryParams(initialFilters)
  );
  const [summary, setSummary] = useState<PromoResultsSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedOffer, setSelectedOffer] = useState<PromoResultsRow | null>(
    null
  );

  async function fetchResults(filters: PromoResultsFilters) {
    setLoading(true);
    try {
      const { data } = await getScheduledOffersResults(filters);
      setSummary(data);
      setAppliedFilters(filters);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'promoResults.error' }));
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    fetchResults(toQueryParams(values));
  }

  function updateValue<K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    fetchResults(toQueryParams(initialFilters));
    // Initial load only; later searches go through the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const results = summary?.results ?? [];
  const sharedOrders = summary?.totals.orders_with_several_promos ?? 0;

  return (
    <>
      <div className="card p-10 w-100">
        <h1 className="mb-8">
          {intl.formatMessage({ id: 'promoResults.title' })}
        </h1>
        <form
          className="d-flex gap-4 align-items-end flex-wrap"
          onSubmit={handleSubmit}
        >
          <DateRangeSelector
            label={intl.formatMessage({ id: 'promoResults.chargeDateRange' })}
            initialSince={initialFilters.since}
            initialUntil={initialFilters.until}
            onSelected={(since, until) =>
              setValues((prev) => ({ ...prev, since, until }))
            }
          />
          <div className="form-group">
            <label htmlFor="market" className="form-label">
              {intl.formatMessage({ id: 'promoResults.market' })}
            </label>
            <select
              id="market"
              className="form-select"
              value={values.market}
              onChange={(e) => updateValue('market', e.target.value)}
            >
              <option value={ALL}>
                {intl.formatMessage({ id: 'promoResults.allMarkets' })}
              </option>
              {PROMO_RESULTS_MARKETS.map((market) => (
                <option key={market} value={market}>
                  {market}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="promoType" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.promoType' })}
            </label>
            <select
              id="promoType"
              className="form-select"
              value={values.promoType}
              onChange={(e) =>
                updateValue(
                  'promoType',
                  e.target.value as FilterValues['promoType']
                )
              }
            >
              <option value={ALL}>
                {intl.formatMessage({ id: 'scheduledOffers.allPromoTypes' })}
              </option>
              {/* Every type, not just PROMO_TYPES: types hidden from the
                  creation form (free shipping) may still have results. */}
              {ALL_PROMO_TYPES.map((type) => (
                <option key={type} value={type}>
                  {intl.formatMessage({ id: PROMO_TYPE_LABEL_IDS[type] })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="state" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.state' })}
            </label>
            <select
              id="state"
              className="form-select"
              value={values.state}
              onChange={(e) =>
                updateValue('state', e.target.value as FilterValues['state'])
              }
            >
              <option value={ALL}>
                {intl.formatMessage({ id: 'scheduledOffers.allStates' })}
              </option>
              <option value="active">
                {intl.formatMessage({ id: 'scheduledOffers.active' })}
              </option>
              <option value="inactive">
                {intl.formatMessage({ id: 'scheduledOffers.inactive' })}
              </option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            <span className="fa fa-search me-2"></span>
            {intl.formatMessage({ id: 'promoResults.search' })}
          </button>
          {loading && (
            <div
              className="spinner-border spinner-border-sm text-primary ms-4"
              role="status"
            />
          )}
        </form>

        {summary && (
          <div className="d-flex gap-4 flex-wrap mt-8">
            <MetricsCard
              title={intl.formatMessage({ id: 'promoResults.globalTotals' })}
              metrics={summary.totals}
              footnote={
                sharedOrders > 0
                  ? intl.formatMessage(
                      { id: 'promoResults.sharedOrdersTotals' },
                      { count: sharedOrders }
                    )
                  : undefined
              }
            />
            {summary.by_market.map((market) => (
              <MetricsCard
                key={market.country}
                title={market.country}
                metrics={market}
              />
            ))}
          </div>
        )}

        {!loading && summary && results.length === 0 && (
          <p className="mt-8">
            {intl.formatMessage({ id: 'promoResults.noOffers' })}
          </p>
        )}
        {results.length > 0 && (
          <div className="table-responsive mt-8">
            <table className="table table-striped align-middle">
              <thead>
                <tr className="fw-bolder">
                  <th>{intl.formatMessage({ id: 'scheduledOffers.name' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'scheduledOffers.promoType' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'promoResults.market' })}</th>
                  <th>{intl.formatMessage({ id: 'promoResults.period' })}</th>
                  <th>{intl.formatMessage({ id: 'scheduledOffers.state' })}</th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.subscribers' })}
                  </th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.orders' })}
                  </th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.grossRevenue' })}
                  </th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.netRevenue' })}
                  </th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.discount' })}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {results.map((offer) => (
                  <tr
                    key={offer.id}
                    className={clsx({ 'opacity-50': offer.orders === 0 })}
                  >
                    <td>
                      <div>{offer.name}</div>
                      <div className="text-muted fs-7">{offer.description}</div>
                    </td>
                    <td>
                      <span className="badge badge-light-primary">
                        {PROMO_TYPE_LABEL_IDS[offer.promo_type]
                          ? intl.formatMessage({
                              id: PROMO_TYPE_LABEL_IDS[offer.promo_type],
                            })
                          : offer.promo_type_display}
                      </span>
                    </td>
                    <td>{offer.country}</td>
                    <td className="text-nowrap">
                      {formatShortDate(intl, offer.valid_from)} –{' '}
                      {formatShortDate(intl, offer.valid_until)}
                    </td>
                    <td>
                      {intl.formatMessage({
                        id:
                          offer.state === 'active'
                            ? 'scheduledOffers.active'
                            : 'scheduledOffers.inactive',
                      })}
                    </td>
                    <td className="text-end">
                      {intl.formatNumber(offer.subscribers)}
                    </td>
                    <td className="text-end">
                      {intl.formatNumber(offer.orders)}
                      {offer.orders_with_other_promos > 0 && (
                        <span
                          className="badge badge-light-warning ms-2"
                          title={intl.formatMessage(
                            { id: 'promoResults.sharedOrdersHint' },
                            { count: offer.orders_with_other_promos }
                          )}
                        >
                          {intl.formatMessage(
                            { id: 'promoResults.sharedOrders' },
                            { count: offer.orders_with_other_promos }
                          )}
                        </span>
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      {formatAmount(
                        intl,
                        offer.gross_revenue,
                        offer.currency_symbol
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      {formatAmount(
                        intl,
                        offer.net_revenue,
                        offer.currency_symbol
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      {formatAmount(
                        intl,
                        offer.discount_inc_tax,
                        offer.currency_symbol
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm px-3 py-2 text-nowrap"
                        disabled={offer.orders === 0}
                        onClick={() => setSelectedOffer(offer)}
                      >
                        <span className="fa fa-list me-2"></span>
                        {intl.formatMessage({ id: 'promoResults.viewOrders' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ScheduledOfferOrdersModal
        offer={selectedOffer}
        dateFilters={{
          date_from: appliedFilters.date_from,
          date_to: appliedFilters.date_to,
        }}
        onClose={() => setSelectedOffer(null)}
      />
    </>
  );
}
